import React from "react";
import { useTranslation } from "react-i18next";
import { Users, UserPlus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import UserAvatar from "@/components/ui/userAvatar";
import InputText from "@/components/common/InputText";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { TaskMemberUser } from "../types";
import type { UserOrganisationType } from "@/api/query/useOrganisationDetailsQuery";
import type { Task } from "@/api/mutation/useTaskCreateMutation";
import { Link } from "react-router-dom";
import MemberAssignmentItem from "../../taskAssignmentItem";

interface TaskAssigneeSectionProps {
  projectId?: string;
  task?: Task;
  assignedMembers: TaskMemberUser[];
  allOrgMembers: UserOrganisationType[];
  onToggleMember: (member: UserOrganisationType, workingHours?: number) => void;
  onUpdateMemberAssignment: (
    member: UserOrganisationType,
    workingHours: number,
    taskEffort: number,
  ) => void;
  allowed: boolean;
  totalWorkHours?: number;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

export const TaskAssigneeSection: React.FC<TaskAssigneeSectionProps> = ({
  projectId,
  task,
  assignedMembers,
  allOrgMembers,
  onToggleMember,
  onUpdateMemberAssignment,
  allowed,
  totalWorkHours,
  scrollContainerRef,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = React.useState("");
  const memberListRef = React.useRef<HTMLDivElement>(null);

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const el = memberListRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const isAtTop = scrollTop <= 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

    // If scrolling down and at the bottom, or scrolling up and at the top, delegate scroll to parent modal container
    if ((e.deltaY > 0 && isAtBottom) || (e.deltaY < 0 && isAtTop)) {
      if (scrollContainerRef?.current) {
        scrollContainerRef.current.scrollTop += e.deltaY;
      }
    }
  };

  const filteredMembers = React.useMemo(() => {
    if (!searchQuery.trim()) return allOrgMembers;
    const query = searchQuery.toLowerCase();
    return allOrgMembers.filter((m) => {
      const fullName = `${m.user.firstName || ""} ${m.user.lastName || ""}`.toLowerCase();
      const email = (m.user.email || "").toLowerCase();
      return fullName.includes(query) || email.includes(query);
    });
  }, [allOrgMembers, searchQuery]);

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-white border border-slate-200/90 rounded-xl shadow-2xs">
      {/* Left: Section Title & Assigned Avatars */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          <Users className="w-4 h-4 text-slate-400" />
          <span>{t("assignees")}</span>
          {typeof totalWorkHours === "number" && totalWorkHours > 0 && (
            <span className="text-[11px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
              {totalWorkHours}h
            </span>
          )}
        </div>

        {/* Avatar Stack */}
        <div className="flex items-center -space-x-2 overflow-hidden py-1">
          {assignedMembers.length > 0 ? (
            assignedMembers
              .filter((m) => Boolean(m && m.userId))
              .slice(0, 5)
              .map((m, idx) => (
                <div
                  key={m?.userId || idx}
                  className="inline-block ring-2 ring-white rounded-full transition-transform hover:scale-110 hover:z-10"
                >
                  <UserAvatar user={m} className="w-7 h-7 text-xs" />
                </div>
              ))
          ) : (
            <span className="text-xs text-slate-400 italic pl-1">
              {t("no_assignees")}
            </span>
          )}

          {assignedMembers.length > 5 && (
            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 ring-2 ring-white text-[11px] font-bold text-slate-600">
              +{assignedMembers.length - 5}
            </div>
          )}
        </div>
      </div>

      {/* Right: Manage Assignees Trigger & Popover */}
      <Popover>
        <PopoverTrigger asChild disabled={!allowed}>
          <Button
            type="button"
            variant="none"
            disabled={!allowed}
            className={`flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold transition-all ${
              allowed
                ? "bg-blue-600 hover:bg-blue-700 text-white shadow-2xs cursor-pointer"
                : "opacity-50 cursor-not-allowed bg-blue-600/50 text-white"
            }`}
          >
            <UserPlus className="w-3.5 h-3.5 text-white" />
            <span>{assignedMembers.length > 0 ? t("manage") : t("assign_member")}</span>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          side="bottom"
          align="end"
          sideOffset={6}
          collisionPadding={16}
          avoidCollisions={true}
          className="w-[650px] max-w-[95vw] max-h-[75vh] flex flex-col p-4 z-[9999] shadow-2xl border border-slate-200/90 rounded-2xl bg-white overflow-hidden"
        >
          <div className="flex flex-col gap-3.5 flex-1 min-h-0">
            {/* Popover Header with Title and Summary Stats */}
            <div className="shrink-0 flex flex-col gap-2.5 pb-3 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    {t("manage_assignees")}
                  </span>
                </div>
                <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2.5 py-0.5 rounded-full">
                  {t("assigned_count", { count: assignedMembers.length })}
                </span>
              </div>

              {/* Total Hours & Total Award/Workload Metric Cards */}
              <div className="grid grid-cols-2 gap-2.5">
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-200/80 rounded-xl">
                  <span className="text-[11px] font-semibold text-slate-500">
                    {t("total_hours")}
                  </span>
                  <span className="text-sm font-bold text-slate-900">
                    {typeof totalWorkHours === "number" ? Math.round(totalWorkHours * 100) / 100 : 0}
                    <span className="text-xs font-medium text-slate-500 ml-1">h</span>
                  </span>
                </div>

                <div className="flex items-center justify-between px-3 py-2 bg-primary-50/50 border border-primary-100 rounded-xl">
                  <span className="text-[11px] font-semibold text-primary-700">
                    {t("total_award")}
                  </span>
                  <span className="text-sm font-bold text-primary-800">
                    {typeof totalWorkHours === "number"
                      ? (Math.round((totalWorkHours / 8) * 100) / 100).toFixed(1)
                      : "0.0"}
                    <span className="text-xs font-medium text-primary-600 ml-1">
                      {t("days")}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Search Input */}
            <div className="shrink-0 relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <InputText
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("filter_by_name_and_email")}
                className="pl-8 h-8 text-xs border-slate-200 rounded-lg focus:ring-1 focus:ring-primary-500"
              />
            </div>

            {/* Member List */}
            <div
              ref={memberListRef}
              onWheel={handleWheel}
              className="flex-1 min-h-0 max-h-[260px] flex flex-col gap-1.5 overflow-y-auto overscroll-contain pr-1"
            >
              {filteredMembers.length > 0 ? (
                filteredMembers.map((memberData) => {
                  const assignedInfo = task?.assignedUsers?.find(
                    (u) => u.user?.userId === memberData.user?.userId,
                  );
                  const isAssigned = !!assignedInfo;

                  return (
                    <MemberAssignmentItem
                      key={memberData.user.userId}
                      task={task as Task}
                      member={memberData}
                      assignedInfo={assignedInfo as unknown as Parameters<typeof MemberAssignmentItem>[0]["assignedInfo"]}
                      isAssigned={isAssigned}
                      taskDuration={task?.duration ?? 1}
                      onToggleAssign={onToggleMember}
                      onUpdateAssign={onUpdateMemberAssignment}
                    />
                  );
                })
              ) : (
                <div className="text-center py-6 text-xs text-slate-400">
                  {t("No members found")}
                </div>
              )}
            </div>

            {/* Add more members link */}
            <div className="shrink-0 pt-2 border-t border-slate-100 text-right">
              <Link
                to={projectId ? `/members/${projectId}` : (task?.projectId ? `/members/${task.projectId}` : "/members")}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-1 underline transition-colors"
              >
                {t("Add more members to your project")} →
              </Link>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default TaskAssigneeSection;
